import React, { useRef, useState, useEffect } from "react";
import { AppLog } from "services";
import newFeedStoriesStore from "store/NewFeedStoriesStore";
import { RouteUrl, StatusMsg } from "utils";
import { Service } from "utils/services";
import ImageUrlProfile from "../image-url/ImageUrlProfile";
import GoogleFontLoader from "react-google-font-loader";
import { Link } from "react-router-dom";
import { ShimmerNewFeedStories } from "../shrimmer/Shrimmer";
import appStore from "store/appStore";
import ImageUrl from "../image-url/ImageUrl";
import { cn } from "lib/utils";

const NewFeedStories: React.FC = () => {
  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showPrev, setShowPrev] = useState(false);
  const { stories, appRecord, initializePage, fetchStories } = newFeedStoriesStore();
  const { onOpenDialogViewStoryModal } = appStore();

  const loadMoreStories = () => {
    fetchStories(false);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const atStart = container.scrollLeft === 0;
      setShowPrev(!atStart);

      const atEnd = container.scrollWidth - container.scrollLeft <= container.clientWidth + 1;
      if (atEnd) loadMoreStories();
    }
  };

  useEffect(() => {
    const loadInitialStory = async () => {
      try {
        initializePage();
      } catch (error) {
        AppLog.show({
          message: `[loadInitialReactions]`,
          status: StatusMsg.error,
          error,
        });
      }
    };

    loadInitialStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scroll = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (container) {
      const scrollAmount = direction === "left" ? -container.clientWidth : container.clientWidth;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });

      setTimeout(() => handleScroll(), 300);
    }
  };

  const _renderStatus = (post: any) => {
    let textPost = JSON.parse(post?.media_style);

    if (!textPost) return <></>;

    let subStringBackgroundColor: string = textPost?.background_color;
    let subStringColor: string = textPost?.text_style?.color;

    if (subStringBackgroundColor.length && subStringColor.length === 8) {
      subStringBackgroundColor = subStringBackgroundColor.substring(2);
      subStringColor = subStringColor.substring(2);
    } else {
      subStringColor = "ffffff";
      subStringBackgroundColor = "000000";
    }

    const textSize = (textPost?.text_style?.font_size * 0.65) / 3;

    return (
      <div
        style={{
          overflow: "hidden",
          backgroundColor: `#${subStringBackgroundColor}`,
        }}
        className="flex w-full h-full items-center text-center justify-center"
      >
        <GoogleFontLoader fonts={[{ font: `${textPost?.text_style?.font}`, weights: [400] }]} />
        <p
          style={{
            color: `#${subStringColor}`,
            fontSize: textSize,
            fontFamily: `${textPost?.text_style?.font}, sans-serif`,
          }}
          className="text-center whitespace-pre-line"
        >
          {textPost?.text}
        </p>
      </div>
    );
  };

  const _renderThumnail = (postAttachment: any) => {
    const width = Service.toDouble(postAttachment.attachment_media_width);
    const height = Service.toDouble(postAttachment.attachment_media_height);
    const ratio = height === 0 ? 1 : width / height;

    return (
      <img
        src={`${postAttachment.attachment_media_picture_file_src}`}
        alt={"Thumnail"}
        className={`w-full h-full  hover:scale-105  ${ratio >= 0 && ratio < 0.74 ? "object-cover" : "object-contain"}`}
      />
    );
  };

  return appRecord.isLoading() ? (
    <ShimmerNewFeedStories />
  ) : (
    <div className="relative mb-4" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {/* Previous Button */}
      {showPrev && (
        <button onClick={() => scroll("left")} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-50 p-2 rounded-full shadow-md hover:bg-opacity-100">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Story List */}
      <div ref={containerRef} className={`flex overflow-x-auto space-x-2 pt-2 no-scrollbar ${hovered ? "scrollbar-visible" : "scrollbar-custom"}`}>
        {stories.map((story, index) => (
          <div key={story.id} className="relative w-24 h-40 flex-shrink-0 cursor-pointer rounded-xl overflow-hidden">
            {story.id === 0 ? (
              <div className="relative w-full h-full rounded-xl overflow-hidden bg-black dark:bg-gray-700">
                {/* Image Section (70%) */}
                <div className="h-[70%] bg-black dark:bg-gray-700">
                  <ImageUrlProfile user={Service.userAuth} className={cn("w-full h-full object-cover")} />
                </div>

                {/* White Bottom Section (30%) */}
                <div className="h-[30%] bg-black dark:bg-gray-700 relative">
                  <div className="mt-6 text-center text-xs font-medium text-white">Create story</div>
                </div>

                {/* Button Positioned at the Intersection */}
                <div className="absolute top-[70%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 p-1.5 rounded-full border-4 border-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              </div>
            ) : (
              <Link
                to={`${RouteUrl.viewStories}?user_id=${story?.user?.id}`}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.button !== 0) {
                    // Allow native open-in-new-tab or right-click behavior
                    return;
                  }

                  // Prevent default single-click nav and open modal instead
                  e.preventDefault();
                  onOpenDialogViewStoryModal(`${story?.user?.id}`, RouteUrl.newsFeed);
                }}
              >
                <div className="w-full h-full bg-black dark:bg-gray-700 ">{story.media_type === "status" ? _renderStatus(story) : _renderThumnail(story.post_attachments[0])}</div>

                <div className={`absolute top-2 left-2 w-8 h-8 rounded-full `}>
                  <ImageUrlProfile user={story.user} className="w-full h-full rounded-full object-cover border shadow-md" isLinkProfile={true} />
                </div>

                <div className="absolute bottom-2 left-2 right-2  ">
                  <p className="text-white text-xs font-semibold break-words line-clamp-2 leading-tight drop-shadow-[1px_1px_0px_rgba(0,0,0,0.5)]">{story?.user.name}</p>
                </div>
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Next Button */}
      <button onClick={() => scroll("right")} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white bg-opacity-50 p-2 rounded-full shadow-md hover:bg-opacity-100">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

export default NewFeedStories;

